import type {
  PlantGardenV2Snapshot,
  PlantNourishmentResultV2,
  PlantRewardResultV2,
  PlantV2MutationResult,
} from '@/entities/plant-garden-v2'
import type { PlantGardenV2Repository } from './PlantGardenV2Repository'

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>

export interface HttpPlantGardenV2AdapterOptions {
  baseUrl?: string
  authMode?: 'demo' | 'proxy'
  fetchImpl?: FetchLike
}

export class HttpPlantGardenV2Adapter implements PlantGardenV2Repository {
  private readonly baseUrl: string
  private readonly authMode: 'demo' | 'proxy'
  private readonly fetchImpl: FetchLike

  constructor(options: HttpPlantGardenV2AdapterOptions = {}) {
    this.baseUrl = (options.baseUrl ?? import.meta.env?.VITE_OPENELP_DATA_API_BASE_URL ?? '').replace(/\/$/, '')
    const authMode = options.authMode ?? import.meta.env?.VITE_OPENELP_DATA_AUTH_MODE ?? 'demo'
    if (authMode !== 'demo' && authMode !== 'proxy') throw new TypeError('VITE_OPENELP_DATA_AUTH_MODE must be demo or proxy')
    this.authMode = authMode
    this.fetchImpl = options.fetchImpl ?? ((input, init) => globalThis.fetch(input, init))
  }

  private async request(path: string, studentId: string, init?: RequestInit): Promise<unknown> {
    const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
      ...init,
      headers: {
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...(this.authMode === 'demo' ? { 'X-Student-Id': studentId } : {}),
        ...init?.headers,
      },
    })
    const body = await response.json() as { error?: { message?: string } | string }
    if (!response.ok) {
      const message = typeof body.error === 'string'
        ? body.error
        : body.error?.message ?? `Plant v2 Data Interface failed (${response.status})`
      const error = new Error(message) as Error & { status?: number; body?: unknown }
      error.status = response.status
      error.body = body
      throw error
    }
    return body
  }

  async loadGarden(studentId: string): Promise<PlantGardenV2Snapshot> {
    const body = await this.request(`/api/plant-garden-v2/${encodeURIComponent(studentId)}`, studentId) as { garden?: unknown }
    return parseSnapshot(body.garden)
  }

  async applyRewardClaim(studentId: string, rewardClaimId: string): Promise<PlantRewardResultV2> {
    return this.request(
      `/api/plant-garden-v2/${encodeURIComponent(studentId)}/reward-events`,
      studentId,
      { method: 'POST', body: JSON.stringify({ rewardClaimId }) },
    ) as Promise<PlantRewardResultV2>
  }

  async applyNourishmentAttempt(studentId: string, attemptId: string): Promise<PlantNourishmentResultV2> {
    return this.request(
      `/api/plant-garden-v2/${encodeURIComponent(studentId)}/nourishment-events`,
      studentId,
      { method: 'POST', body: JSON.stringify({ attemptId }) },
    ) as Promise<PlantNourishmentResultV2>
  }

  plantSeed(studentId: string, input: Parameters<PlantGardenV2Repository['plantSeed']>[1]): Promise<PlantV2MutationResult> {
    return this.command(studentId, 'plants', input)
  }

  moveTree(studentId: string, input: Parameters<PlantGardenV2Repository['moveTree']>[1]): Promise<PlantV2MutationResult> {
    return this.command(studentId, 'moves', input)
  }

  uprootDeadTree(studentId: string, input: Parameters<PlantGardenV2Repository['uprootDeadTree']>[1]): Promise<PlantV2MutationResult> {
    return this.command(studentId, 'uproots', input)
  }

  unlockNextParcel(studentId: string, expectedRevision: number): Promise<PlantV2MutationResult> {
    return this.command(studentId, 'parcels/unlock', { expectedRevision })
  }

  private command(studentId: string, path: string, body: object): Promise<PlantV2MutationResult> {
    return this.request(
      `/api/plant-garden-v2/${encodeURIComponent(studentId)}/${path}`,
      studentId,
      { method: 'POST', body: JSON.stringify(body) },
    ) as Promise<PlantV2MutationResult>
  }
}

function parseSnapshot(value: unknown): PlantGardenV2Snapshot {
  if (!value || typeof value !== 'object') throw new TypeError('Plant v2 Data Interface returned an invalid garden')
  const candidate = value as Partial<PlantGardenV2Snapshot>
  if (candidate.version !== 2
      || !Number.isSafeInteger(candidate.revision)
      || !Number.isSafeInteger(candidate.lifetimeExp)
      || !Array.isArray(candidate.instances)
      || !Array.isArray(candidate.legacySnapshots)
      || !Array.isArray(candidate.parcels)
      || !Array.isArray(candidate.placements)) {
    throw new TypeError('Plant v2 Data Interface returned an invalid garden')
  }
  return candidate as PlantGardenV2Snapshot
}
